import { BaselineSection } from '../BaselineSection';
import { type StepForm, WizardField } from '../WizardField';

interface Props {
  form: StepForm;
}

const BASELINE_FIELDS = [
  {
    name: 'step2.baselineTotalConstructionSpending',
    label: 'Existing construction spending (baseline)',
    format: 'currency' as const,
    hint: 'Prior construction at the site, if any',
  },
  { name: 'step2.baselineLand', label: 'Existing land', format: 'currency' as const },
  { name: 'step2.baselineBuildings', label: 'Existing buildings', format: 'currency' as const },
  {
    name: 'step2.baselineMachineryEquipment',
    label: 'Existing machinery & equipment',
    format: 'currency' as const,
  },
  { name: 'step2.baselineFfe', label: 'Existing FF&E', format: 'currency' as const },
  { name: 'step2.baselineInventory', label: 'Existing inventory', format: 'currency' as const },
];

export function Step2CapitalInvestment({ form }: Props) {
  const projectType = form.watch('step1.projectType');

  // Live total of the capital breakdown (separate from construction spending).
  const breakdown = form.watch([
    'step2.land',
    'step2.buildings',
    'step2.machineryEquipment',
    'step2.ffe',
    'step2.inventory',
  ]);
  const capitalTotal = breakdown.reduce((sum: number, v) => sum + (Number(v) || 0), 0);

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-base font-semibold text-slate-800">Capital Investment</h3>
        <p className="mt-0.5 text-sm text-slate-500">
          Construction spending drives the construction-phase impact. The capital breakdown below
          drives the project&rsquo;s assessed value. They measure different things and do not need
          to add up to each other.
        </p>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="col-span-2">
          <WizardField
            form={form}
            name="step2.totalConstructionSpending"
            label="Total construction spending"
            format="currency"
            hint="Construction-phase cost (buildings + site work). Separate from the breakdown below."
          />
        </div>

        <WizardField form={form} name="step2.land" label="Land" format="currency" />

        <WizardField form={form} name="step2.buildings" label="Buildings" format="currency" />

        <WizardField
          form={form}
          name="step2.machineryEquipment"
          label="Machinery & equipment"
          format="currency"
        />

        <WizardField
          form={form}
          name="step2.ffe"
          label="Furniture, fixtures & equipment"
          format="currency"
        />

        <WizardField
          form={form}
          name="step2.inventory"
          label="Initial inventory"
          format="currency"
        />

        <WizardField
          form={form}
          name="step2.constructionDurationMonths"
          label="Construction duration (months)"
          type="number"
          min={1}
          max={120}
          step={1}
        />
      </div>

      <div className="flex items-center justify-between rounded-md border border-slate-200 bg-slate-50 px-4 py-2 text-sm">
        <span className="text-slate-600">
          Total capital investment (Land + Buildings + Machinery & equipment + FF&amp;E + Inventory)
        </span>
        <span className="font-semibold text-slate-900">
          {capitalTotal.toLocaleString('en-US', {
            style: 'currency',
            currency: 'USD',
            maximumFractionDigits: 0,
          })}
        </span>
      </div>

      <BaselineSection form={form} projectType={projectType} fields={BASELINE_FIELDS} />
    </div>
  );
}
